import { SxProps, Theme } from "@mui/material";
import { keyframes } from "@mui/system";

const modernPulse = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1) drop-shadow(0 0 20px rgba(26, 35, 126, 0.8));
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.4) drop-shadow(0 0 30px rgba(74, 20, 140, 0.9));
  }
  100% {
    transform: scale(1);
    filter: brightness(1) drop-shadow(0 0 20px rgba(26, 35, 126, 0.8));
  }
`;

const smoothScaleIn = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const ringPulse = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.4;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
`;

export const countdownContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  minHeight: "100vh",
  padding: "20px",
  textAlign: "center",
  background:
    "linear-gradient(180deg, rgba(40, 201, 220, 0.1) 0%, rgba(123, 73, 193, 0.1) 100%)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)",
    animation: `${gradientShift} 3s ease-in-out infinite`,
    pointerEvents: "none",
  },
};

export const countdownNumberContainerStyles: SxProps = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: { xs: "200px", sm: "280px" },
  height: { xs: "200px", sm: "280px" },
  marginBottom: { xs: "30px", sm: "50px" },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background:
      "linear-gradient(180deg, rgba(40, 201, 220, 0.2) 0%, rgba(123, 73, 193, 0.2) 100%)",
    animation: `${ringPulse} 2s ease-out infinite`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "3px solid transparent",
    background: "linear-gradient(180deg, #28C9DC, #7B49C1) border-box",
    backgroundClip: "padding-box",
    opacity: 0.8,
  },
};

export const countdownNumberStyles: SxProps = {
  fontSize: { xs: "100px", sm: "140px" },
  fontWeight: "900",
  background: "linear-gradient(180deg, #1a237e 0%, #4a148c 50%, #6a1b9a 100%)",
  backgroundSize: "200% 200%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${modernPulse} 1.2s ease-in-out infinite, ${gradientShift} 3s ease-in-out infinite`,
  userSelect: "none",
  textShadow: "0 0 30px rgba(26, 35, 126, 0.6)",
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  letterSpacing: "-0.02em",
};

export const countdownStartTextStyles: SxProps = {
  fontSize: { xs: "80px", sm: "120px" },
  fontWeight: "900",
  background: "linear-gradient(180deg, #1a237e 0%, #4a148c 50%, #6a1b9a 100%)",
  backgroundSize: "200% 200%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${smoothScaleIn} 0.6s ease-out, ${gradientShift} 2s ease-in-out infinite`,
  userSelect: "none",
  textShadow: "0 0 30px rgba(26, 35, 126, 0.6)",
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

export const countdownInstructionTextStyles: SxProps = {
  fontSize: "28px",
  fontWeight: "500",
  background: "linear-gradient(135deg, #666 0%, #999 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginTop: "30px",
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  letterSpacing: "0.01em",
  opacity: 0.9,
};

export const countdownCircleStyles: SxProps = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "200px", sm: "280px" },
  height: { xs: "200px", sm: "280px" },
  borderRadius: "50%",
  border: "2px solid transparent",
  background:
    "linear-gradient(135deg, rgba(25, 118, 212, 0.3), rgba(76, 175, 80, 0.3)) border-box",
  backgroundClip: "padding-box",
  opacity: 0.6,
};
